import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { Button, Space, message, Modal, Input, Checkbox as AntCheckbox } from 'antd';
import { ColumnHeightOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { invoiceService, Invoice } from '../services/invoices';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

type SortOrder = 'ascend' | 'descend' | null;

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onResizeStop?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onResizeStop,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      onResizeStop={(e, data) => {
        if (!onResizeStop) {
          return;
        }
        const newWidth = Math.max(data.size.width, 50);
        onResizeStop(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const Invoices: React.FC = () => {
  const listingKey = 'invoices';
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnsSearch, setColumnsSearch] = useState('');
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState<React.Key[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const compareReferenceNumbers = (a?: string, b?: string) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };
  const { user } = useAuth();
  const canView = hasPermission(user, 'billing.invoices.view');

  useEffect(() => {
    if (!canView) {
      setInvoices([]);
      return;
    }
    fetchData();
  }, [canView]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Invoices
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await invoiceService.getAll();
      const { items, total } = normalizeInvoicesResponse(response);
      setInvoices(items);
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
    } catch {
      message.error('Failed to load invoices data');
    } finally {
      setLoading(false);
    }
  };

  const normalizeInvoicesResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const nextPageExists = false;
    return { items, nextPageExists, total };
  };

  const handleDelete = async (id: number) => {
    try {
      await invoiceService.delete(id);
      message.success('Invoice deleted');
      fetchData();
    } catch {
      message.error('Failed to delete invoice');
    }
  };

  const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-');

  const getCustomerDisplayName = (record: Invoice) => {
    if (record.business) {
      return record.business.name || record.customer?.name || '-';
    }
    return record.customer?.name || '-';
  };

  const getPrimaryContact = (record: Invoice) => {
    if (record.business) {
      return record.business.contacts?.[0];
    }
    return record.customer?.contacts?.[0];
  };

  const getAddressLabel = (record: Invoice) => {
    const contact: any = getPrimaryContact(record);
    if (!contact) {
      return '-';
    }
    if (record.business) {
      const parts = [contact.main_address1, contact.main_address2].filter(Boolean);
      return parts.length ? parts.join(' ') : '-';
    }
    const parts = [contact.address1, contact.address2].filter(Boolean);
    return parts.length ? parts.join(' ') : '-';
  };

  const getEmailLabel = (record: Invoice) => {
    if (record.business) {
      return (record.business as any)?.email || record.business.contacts?.[0]?.email || '-';
    }
    return record.customer?.email || record.customer?.contacts?.[0]?.email || '-';
  };

  const getContactLabel = (record: Invoice) => {
    const contact: any = getPrimaryContact(record);
    return contact?.contact_number || contact?.email || '-';
  };

  const getAdvisorLabel = (record: Invoice) => {
    if (record.business) {
      return (
        record.business?.advisorStaff?.name ||
        (record.business as any)?.advisor_staff?.name ||
        record.agent?.name ||
        '-'
      );
    }
    return (
      record.customer?.advisorStaff?.name ||
      (record.customer as any)?.advisor_staff?.name ||
      record.agent?.name ||
      '-'
    );
  };

  const getTotalTasksCount = (record: Invoice) => (record.items || []).length;

  const getTotalTasksLabel = (record: Invoice) => {
    const count = getTotalTasksCount(record);
    return count ? String(count) : '-';
  };

  const getInvoiceTotalValue = (record: Invoice) => {
    if (typeof record.total_amount === 'number') {
      return record.total_amount;
    }
    const items = record.items || [];
    if (!items.length) {
      return null;
    }
    return items.reduce((sum, item: any) => sum + Number(item.total_price || 0), 0);
  };

  const getAmountLabel = (record: Invoice) => {
    const total = getInvoiceTotalValue(record);
    if (total == null) {
      return '-';
    }
    return Number.isFinite(total) ? total.toFixed(2) : '-';
  };

  const getTaskNoLabel = (record: Invoice) => record.quotation?.task_no || '-';

  const getTaskDescriptionLabel = (record: Invoice) => record.quotation?.task_description || '-';

  const getPaymentsTotal = (record: Invoice, type: 'official_receipt' | 'credit_note') => {
    const payments = record.payments || [];
    const total = payments
      .filter((payment) => payment.type === type)
      .reduce((sum, payment) => sum + Number(payment.payment_amount || 0), 0);
    return total;
  };

  const getPaymentLabel = (record: Invoice, type: 'official_receipt' | 'credit_note') => {
    const total = getPaymentsTotal(record, type);
    return total ? total.toFixed(2) : '-';
  };

  const getOutstandingLabel = (record: Invoice) => {
    const total = getInvoiceTotalValue(record);
    if (total == null) {
      return '-';
    }
    const receipts = getPaymentsTotal(record, 'official_receipt');
    const credits = getPaymentsTotal(record, 'credit_note');
    const outstanding = total - receipts - credits;
    return Number.isFinite(outstanding) ? outstanding.toFixed(2) : '-';
  };

  const getOutstandingValue = (record: Invoice) => {
    const total = getInvoiceTotalValue(record);
    if (total == null) {
      return null;
    }
    const receipts = getPaymentsTotal(record, 'official_receipt');
    const credits = getPaymentsTotal(record, 'credit_note');
    return total - receipts - credits;
  };

  const baseColumns = useMemo(
    () => [
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      sorter: (a: Invoice, b: Invoice) => getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b)),
      render: (_: unknown, record: Invoice) => getCustomerDisplayName(record),
    },
    {
      title: 'Address',
      key: 'address',
      width: 220,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getAddressLabel(a).localeCompare(getAddressLabel(b)),
      render: (_: unknown, record: Invoice) => getAddressLabel(record),
    },
    {
      title: 'Email',
      key: 'email',
      width: 180,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getEmailLabel(a).localeCompare(getEmailLabel(b)),
      render: (_: unknown, record: Invoice) => getEmailLabel(record),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 160,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getContactLabel(a).localeCompare(getContactLabel(b)),
      render: (_: unknown, record: Invoice) => getContactLabel(record),
    },
    { 
      title: 'Invoice No.', 
      dataIndex: 'invoice_number', 
      key: 'invoice_number', 
      width: 180,
      sorter: (a: Invoice, b: Invoice) =>
        compareReferenceNumbers(a.invoice_number || '', b.invoice_number || ''),
    },
    {
      title: 'Quotation No.',
      dataIndex: 'quotation',
      key: 'quotation',
      width: 150,
      sorter: (a: Invoice, b: Invoice) =>
        compareReferenceNumbers(a.quotation?.quotation_number || '', b.quotation?.quotation_number || ''),
      render: (quotation: any) => quotation?.quotation_number || '-',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a: Invoice, b: Invoice) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      },
      render: (date: string) => formatDateValue(date),
    },
    {
      title: 'Advisor',
      key: 'advisor',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => getAdvisorLabel(a).localeCompare(getAdvisorLabel(b)),
      render: (_: unknown, record: Invoice) => getAdvisorLabel(record),
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (team: any) => team?.name || '-',
    },
    {
      title: 'Staff',
      dataIndex: 'staff',
      key: 'staff',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => (a.staff?.name || '').localeCompare(b.staff?.name || ''),
      render: (staff: any) => staff?.name || '-',
    },
    {
      title: 'Task No.',
      key: 'task_no',
      width: 140,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getTaskNoLabel(a).localeCompare(getTaskNoLabel(b)),
      render: (_: unknown, record: Invoice) => getTaskNoLabel(record),
    },
    {
      title: 'Description',
      key: 'task_description',
      width: 220,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getTaskDescriptionLabel(a).localeCompare(getTaskDescriptionLabel(b)),
      render: (_: unknown, record: Invoice) => getTaskDescriptionLabel(record),
    },
    {
      title: 'Total Tasks',
      key: 'total_tasks',
      width: 130,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getTotalTasksCount(a) - getTotalTasksCount(b),
      render: (_: unknown, record: Invoice) => getTotalTasksLabel(record),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => {
        const aValue = getInvoiceTotalValue(a) ?? 0;
        const bValue = getInvoiceTotalValue(b) ?? 0;
        return aValue - bValue;
      },
      render: (_: unknown, record: Invoice) => getAmountLabel(record),
    },
    {
      title: 'Official Receipt',
      key: 'official_receipt',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => getPaymentsTotal(a, 'official_receipt') - getPaymentsTotal(b, 'official_receipt'),
      render: (_: unknown, record: Invoice) => getPaymentLabel(record, 'official_receipt'),
    },
    {
      title: 'Credit Note',
      key: 'credit_note',
      width: 140,
      visible: false,
      sorter: (a: Invoice, b: Invoice) => getPaymentsTotal(a, 'credit_note') - getPaymentsTotal(b, 'credit_note'),
      render: (_: unknown, record: Invoice) => getPaymentLabel(record, 'credit_note'),
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      width: 140,
      sorter: (a: Invoice, b: Invoice) => {
        const aValue = getOutstandingValue(a) ?? 0;
        const bValue = getOutstandingValue(b) ?? 0;
        return aValue - bValue;
      },
      render: (_: unknown, record: Invoice) => getOutstandingLabel(record),
    },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const columnGroups = useMemo(
    () => [
      {
        title: 'Customer Information',
        keys: ['customer', 'address', 'email', 'contact'],
      },
      {
        title: 'Invoice Details',
        keys: ['invoice_number', 'quotation', 'date', 'advisor', 'team', 'staff'],
      },
      {
        title: 'Billing Information',
        keys: ['task_no', 'task_description', 'amount'],
      },
      {
        title: 'Status',
        keys: ['official_receipt', 'credit_note', 'outstanding'],
      },
    ],
    []
  );
  const defaultVisibleKeys = useMemo(
    () => baseColumns.filter((col) => !('visible' in col) || (col as { visible?: boolean }).visible !== false).map((col) => col.key),
    [baseColumns]
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );
  const columnSelectionLocked = pendingVisibleKeys.length >= maxVisibleColumns;
  const filteredColumnConfig = useMemo(() => {
    if (!columnsSearch.trim()) {
      return columnConfig;
    }
    const query = columnsSearch.toLowerCase();
    return columnConfig.filter((col) => col.title.toLowerCase().includes(query));
  }, [columnConfig, columnsSearch]);
  const columnConfigByKey = useMemo(
    () => new Map(columnConfig.map((col) => [col.key, col])),
    [columnConfig]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.key === 'customer') return (record: Invoice) => getCustomerDisplayName(record);
    if (col.key === 'address') return (record: Invoice) => getAddressLabel(record);
    if (col.key === 'email') return (record: Invoice) => getEmailLabel(record);
    if (col.key === 'contact') return (record: Invoice) => getContactLabel(record);
    if (col.dataIndex === 'invoice_number') return (record: Invoice) => record.invoice_number;
    if (col.dataIndex === 'quotation') return (record: Invoice) => record.quotation?.quotation_number;
    if (col.dataIndex === 'date') return (record: Invoice) => formatDateValue(record.date);
    if (col.key === 'advisor') return (record: Invoice) => getAdvisorLabel(record);
    if (col.dataIndex === 'team') return (record: Invoice) => record.team?.name;
    if (col.dataIndex === 'staff') return (record: Invoice) => record.staff?.name;
    if (col.key === 'task_no') return (record: Invoice) => getTaskNoLabel(record);
    if (col.key === 'task_description') return (record: Invoice) => getTaskDescriptionLabel(record);
    if (col.key === 'total_tasks') return (record: Invoice) => getTotalTasksLabel(record);
    if (col.key === 'amount') return (record: Invoice) => getAmountLabel(record);
    if (col.key === 'official_receipt') return (record: Invoice) => getPaymentLabel(record, 'official_receipt');
    if (col.key === 'credit_note') return (record: Invoice) => getPaymentLabel(record, 'credit_note');
    if (col.key === 'outstanding') return (record: Invoice) => getOutstandingLabel(record);
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due')) {
        return (record: Invoice) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due')) {
      return (record: Invoice) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: Invoice) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Invoice>(
    invoices,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalValue = filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'invoice' : 'invoices'}`;
  const totalAmountValue = useMemo(
    () => filteredData.reduce((sum, record) => sum + (getInvoiceTotalValue(record) ?? 0), 0),
    [filteredData]
  );
  const totalOfficialReceiptValue = useMemo(
    () => filteredData.reduce((sum, record) => sum + getPaymentsTotal(record, 'official_receipt'), 0),
    [filteredData]
  );
  const totalCreditNoteValue = useMemo(
    () => filteredData.reduce((sum, record) => sum + getPaymentsTotal(record, 'credit_note'), 0),
    [filteredData]
  );
  const totalOutstandingValue = useMemo(
    () => filteredData.reduce((sum, record) => sum + (getOutstandingValue(record) ?? 0), 0),
    [filteredData]
  );
  const formatRMTotal = (value: number) => `TOTAL: RM ${value.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const amountTotalLabel = formatRMTotal(totalAmountValue);
  const officialReceiptTotalLabel = formatRMTotal(totalOfficialReceiptValue);
  const creditNoteTotalLabel = formatRMTotal(totalCreditNoteValue);
  const outstandingTotalLabel = formatRMTotal(totalOutstandingValue);
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);


  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Invoice>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  const getCellValue = (record: Invoice, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div />
        <Space>
          <Button
            icon={<ColumnHeightOutlined />}
            onClick={() => {
              setPendingVisibleKeys(visibleKeys);
              setColumnsSearch('');
              setColumnsModalOpen(true);
            }}
          >
            Columns
          </Button>
        </Space>
      </div>
      <Modal
        title="Select Columns"
        open={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        width={820}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="link"
              onClick={() => {
                setPendingVisibleKeys(defaultVisibleKeys);
              }}
            >
              Reset to default
            </Button>
            <Space>
              <Button onClick={() => setColumnsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleColumnVisibilityChangeLimited(pendingVisibleKeys);
                  setColumnsModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Space>
          </div>
        )}
      >
        <Input
          placeholder="Search Columns"
          prefix={<SearchOutlined />}
          value={columnsSearch}
          onChange={(event) => setColumnsSearch(event.target.value)}
          style={{ marginBottom: 16 }}
        />
        {columnGroups.map((group) => {
          const groupColumns = group.keys
            .map((key) => columnConfigByKey.get(key))
            .filter((col): col is typeof columnConfig[number] => Boolean(col))
            .filter((col) => filteredColumnConfig.some((filtered) => filtered.key === col.key));

          if (groupColumns.length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                {group.title.toUpperCase()}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {groupColumns.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AntCheckbox
                      checked={pendingVisibleKeys.includes(col.key)}
                      disabled={columnSelectionLocked && !pendingVisibleKeys.includes(col.key)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setPendingVisibleKeys((prev) => [...prev, col.key]);
                        } else {
                          setPendingVisibleKeys((prev) => prev.filter((key) => key !== col.key));
                        }
                      }}
                    />
                    <span style={{ color: columnSelectionLocked && !pendingVisibleKeys.includes(col.key) ? '#9ca3af' : undefined }}>
                      {col.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Modal>

      <div className="flowbite-listing-table-shell">
        <div style={{ paddingRight: scrollbarWidth }}>
          <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={sortedData.length > 0 && selectedRowKeys.length === sortedData.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRowKeys(sortedData.map((row) => row.id));
                      } else {
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  const isSortable = typeof col.sorter === 'function';
                  const isSorted = sortKey === key;
                  const sortIndicator =
                    isSorted && sortOrder
                      ? sortOrder === 'ascend'
                        ? '▲'
                        : '▼'
                      : '';
                  const isFirstDataColumn = index === 0;
                  const maxWidth =
                    availableWidth != null
                      ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                      : undefined;

                  return (
                    <FlowbiteResizableHeader
                      key={key}
                      width={columnWidths[index]}
                      maxWidth={maxWidth}
                      onResize={handleResize(index)}
                      onClick={() => handleSort(key, col.sorter)}
                      className={isSortable ? 'cursor-pointer select-none' : undefined}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div>{col.title}</div>
                          {isFirstDataColumn && (
                            <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                          )}
                          {key === 'amount' && (
                            <div className="mt-1 text-xs text-gray-400">{amountTotalLabel}</div>
                          )}
                          {key === 'official_receipt' && (
                            <div className="mt-1 text-xs text-gray-400">{officialReceiptTotalLabel}</div>
                          )}
                          {key === 'credit_note' && (
                            <div className="mt-1 text-xs text-gray-400">{creditNoteTotalLabel}</div>
                          )}
                          {key === 'outstanding' && (
                            <div className="mt-1 text-xs text-gray-400">{outstandingTotalLabel}</div>
                          )}
                        </div>
                        {isSortable && (
                          <span className="text-xs text-gray-400">{sortIndicator}</span>
                        )}
                      </div>
                    </FlowbiteResizableHeader>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableHeadCell className="w-12" />
                {tableColumns.map((col, index) => {
                  const key = String(col.key);
                  return (
                    <TableHeadCell key={`${key}-search`} className="py-2">
                      {searchRowCells?.[index] ?? null}
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>
          </FlowbiteTable>
        </div>
        <div
          className="flowbite-listing-table-scroll"
          ref={tableScrollRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          style={
            viewportHeight
              ? {
                  height: viewportHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : { overflowY: 'auto', overflowX: 'hidden' }
          }
        >
          <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
            <colgroup>
              <col style={{ width: 48 }} />
              {tableColumns.map((col, index) => (
                <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
              ))}
            </colgroup>
            <TableBody className="divide-y">
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                    No invoices
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedData.length > 0 && (
                <>
                  {visibleRange.topPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.topPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                  {visibleRows.map((record: Invoice, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={canView ? () => navigate(`/invoices/${record.id}/edit`) : undefined}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => {
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content =
                            typeof col.render === 'function'
                              ? col.render(value, record, index)
                              : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {visibleRange.bottomPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.bottomPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </FlowbiteTable>
        </div>
      </div>
    </div>
  );
};
